import { getConfigForMeasuredTest } from 'Components/Worksheets/Config';
import {
  mergeConfig,
  wrapAsDerivedFromMeasurementsConfig as wrap,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import { FdMeasurementApplication } from 'Components/Worksheets/NuclearFieldDensityWorksheet/types';
import getMeasuredUsingConfig from 'Components/Worksheets/SandReplacementWorksheet/measuredUsingConfig';
import { FdReportResultsOption } from 'Components/Worksheets/SandReplacementWorksheet/types';
import qestIds from '../../../QestIds';
import { getMoistureContentPercentFormat } from './../../../MoistureContentWorksheet/Bs/Config';
import getLabelConfig from './labelConfig';

const defaultReadability = { defaultReadability: 1, defaultUom: 'g' };
const massConfig = { uom: 'g', format: { nearest: 0 } };
const densityConfig = { uom: 'Mg/m³', format: { nearest: 0.001 } };

const getConfig = (prefix: string) =>
  mergeConfig(
    {
      ...getConfigForMeasuredTest(
        qestIds.moistureContent.BS_1377_2.rev1990,
        'data.EmbeddedMoistureContent.MoistureContentTest'
      ),

      [`${prefix}.Preparation.InternalDiameter`]: { uom: 'mm', format: { nearest: 0.1 } },
      [`${prefix}.Preparation.CalibrationInitialMassOfSand`]: massConfig,
      [`${prefix}.Preparation.CalibrationMassOfSandInCone`]: massConfig,
      [`${prefix}.Preparation.BulkDensityOfSand`]: densityConfig,
      [`${prefix}.Preparation.MeasurementApplication`]: {
        options: [
          { label: 'Absolute value tests', value: FdMeasurementApplication.AbsoluteTests },
          { label: 'Compliance test for compacted material', value: FdMeasurementApplication.ComplianceTest },
        ],
      },

      [`${prefix}.Measurement.CalibrationInitialMassOfSand`]: massConfig,
      [`${prefix}.Measurement.InitialMassOfSand`]: massConfig,
      [`${prefix}.Measurement.MassOfSandInCone`]: massConfig,
      [`${prefix}.Measurement.CalculatedBulkDensityOfSand`]: densityConfig,

      [`${prefix}.Results.MoistureContent`]: { format: getMoistureContentPercentFormat },
      [`${prefix}.Results.DryDensity`]: densityConfig,
      [`${prefix}.Results.MaximumDryDensity`]: densityConfig,
      [`${prefix}.Results.OptimumMoistureContent`]: { format: { nearest: 0.1 } },
      [`${prefix}.Results.RelativeCompaction`]: { format: { nearest: 0.1 } },
      [`${prefix}.Results.AirVoids`]: { format: { nearest: 0.1 } },

      [`${prefix}.Results.ReportResultsOption`]: {
        options: [
          { label: 'Relative Compaction & Air Voids', value: FdReportResultsOption.RelativeCompactionAndAirVoids },
          { label: 'Relative Compaction Only', value: FdReportResultsOption.RelativeCompactionOnly },
          { label: 'Air Voids Only', value: FdReportResultsOption.AirVoidsOnly },
        ],
      },
      [`${prefix}.Retest.OriginalTest.ImportedTest.Results.RelativeCompaction`]: { format: { nearest: 0.1 } },
      [`${prefix}.Retest.OriginalTest.ImportedTest.Results.AirVoids`]: { format: { nearest: 0.1 } },

      [`${prefix}.EmbeddedMoistureContent.LabDryDensity`]: densityConfig,

      [`${prefix}.IsParticleDensityAssumed`]: {
        options: [
          { label: 'Assumed', value: true },
          { label: 'Imported', value: false },
        ],
      },
      [`${prefix}.ParticleDensityAssumed`]: { uom: 'Mg/m³', format: { nearest: 0.01 } },
      [`${prefix}.ReferenceDensityResults.ParticleDensity`]: { uom: 'Mg/m³', format: { nearest: 0.01 } },
      [`${prefix}.ReferenceDensityResults.MaximumDryDensity`]: { uom: 'Mg/m³', format: { nearest: 0.01 } },
      [`${prefix}.ReferenceDensityResults.OptimumMoistureContent`]: { format: { nearest: 0.1 } },

      [`${prefix}.Preparation.Balance`]: defaultReadability,
    },
    getMeasuredUsingConfig(prefix),
    getDerivedFromMeasurementsConfig(prefix),
    getLabelConfig(prefix)
  );

const getDerivedFromMeasurementsConfig = (prefix: string) => ({
  [`${prefix}.Measurement.MassOfSandInHole`]: wrap([
    `${prefix}.Measurement.InitialMassOfSandA`,
    `${prefix}.Measurement.MassOfSandAfterPouring`,
    `${prefix}.Measurement.MassOfSandInCone`,
  ]),
});

export default {
  [qestIds.sandReplacement.BS_1377_9_Clause_2_2.rev1990]: getConfig,
};
